/**
 * Единый клиент для работы с API
 */

class ApiClient {
  constructor(baseUrl) {
    this.baseUrl = baseUrl;
  }

  /**
   * Базовый метод для выполнения запросов
   * @param {string} endpoint - Эндпоинт (начинается с /)
   * @param {Object} options - Опции fetch
   * @returns {Promise<any>} - Данные от сервера
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    console.log(`[REQUEST] ${options.method || 'GET'} ${url}`);

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...options.headers,
        },
      });

      console.log(`[RESPONSE] ${response.status} ${response.statusText}`);

      if (!response.ok) {
        const result = await this.parseResponse(response);
        console.log(`[ERROR RESPONSE]`, result);
        throw this.createError(result, response.status);
      }

      const result = await this.parseResponse(response);
      console.log(`[SUCCESS] ${options.method || 'GET'} ${endpoint}:`, result);
      return result;
    
    } catch (error) {
      console.error(`[FETCH ERROR] ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * Парсинг ответа с обработкой разных типов
   */
  async parseResponse(response) {
    const contentType = response.headers.get('content-type');
    
    if (contentType && contentType.includes('application/json')) {
      try {
        return await response.json();
      } catch (e) {
        console.warn('Failed to parse JSON:', e);
        return null;
      }
    }
    
    // Для не-JSON ответов возвращаем response (например, для файлов)
    return response;
  }
  
  /**
   * Создание стандартизированной ошибки
   */
  createError(result, status) {
    const message = result?.message || this.getStatusMessage(status);
    const error = new Error(message);
    error.status = status;
    error.originalError = result;
    return error;
  }

  /**
   * Получение сообщения по HTTP статусу
   */
  getStatusMessage(status) {
    const messages = {
      400: 'Некорректный запрос',
      401: 'Необходима авторизация',
      403: 'Доступ запрещен',
      404: 'Ресурс не найден',
      500: 'Внутренняя ошибка сервера',
    };
    return messages[status] || `Ошибка HTTP ${status}`;
  }

  /**
   * GET запрос
   */
  get(endpoint, params = null) {
    let url = endpoint;
    if (params) {
      const queryString = new URLSearchParams(params).toString();
      url = `${endpoint}?${queryString}`;
    }
    return this.request(url, { method: 'GET' });
  }

  /**
   * POST запрос (для JSON данных)
   */
  post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  /**
   * POST запрос для FormData (загрузка файлов)
   */
  postFormData(endpoint, formData) {
    return this.request(endpoint, {
      method: 'POST',
      body: formData,
      // НЕ устанавливаем Content-Type - браузер установит его с правильным boundary
    });
  }

  /**
   * PUT запрос
   */
  put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  /**
   * PATCH запрос
   */
  patch(endpoint, data) {
    return this.request(endpoint, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  /**
   * DELETE запрос
   */
  delete(endpoint) {
    return this.request(endpoint, { 
      method: 'DELETE' 
    });
  }

  /**
   * Загрузка файла (устаревший метод, используйте postFormData)
   */
  async upload(endpoint, file, fieldName = 'file') {
    const formData = new FormData();
    formData.append(fieldName, file);
    return this.postFormData(endpoint, formData);
  }
}

// Создаем и экспортируем единственный экземпляр клиента
export const apiClient = new ApiClient(process.env.REACT_APP_API_URL || 'http://localhost:8080');

// Экспортируем класс для возможности создания дополнительных инстансов
export default ApiClient;